import {
  Box,
  CloseButton,
  Stack,
  Tab,
  TabIndicator,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin, ToolbarProps } from '@react-pdf-viewer/default-layout';
import { getFilePlugin } from '@react-pdf-viewer/get-file';
import { DOCUMENT_TYPE } from 'helpers/enum';
import {
  getOfficeLivePreviewURL,
  isMarkdownFormat,
  isOfficeSupportedType,
  isPDFFormat,
  isSeperatedValueFormat,
  isTxtFormat,
} from 'helpers/file';
import { HTTPMethod } from 'helpers/method';
import { t } from 'i18next';
import Papa from 'papaparse';
import React, { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { request } from 'services/configs/axiosConfig';
import { Citation } from 'types/reducers/conversation';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import jumpToPagePlugin from './jumpToPagePlugin';
import renderError from './renderError';
import renderLoader from './renderLoader';
import transformToolbar from './toolbarConfig';

interface FilePreviewProps {
  data: Citation;
  onClose: () => void;
  isValid: boolean;
}
type FilePreviewRenderProps = {
  isValid: boolean;
  data: any;
  csvContent?: string[][];
  markdownContent?: string;
  isShowing?: boolean;
  currentPage?: number;
};
const FilePreviewRender = ({
  isValid,
  data,
  csvContent,
  markdownContent,
  currentPage,
  isShowing,
}: FilePreviewRenderProps) => {
  const { fileType, file_url, file_name } = data;
  const realCurrentPage = (currentPage ?? 1) - 1;

  const getFilePluginInstance = getFilePlugin({
    fileNameGenerator: (file) => {
      let fileName = file.name.substring(file.name.lastIndexOf('/') + 1);
      fileName = fileName.split('?')[0];
      fileName = fileName.substring(fileName.indexOf('_') + 1);
      fileName = decodeURIComponent(fileName);
      return `${fileName}`;
    },
  });

  const { Download } = getFilePluginInstance;

  const renderToolbar = (Toolbar: (_props: ToolbarProps) => React.ReactElement) => (
    <Toolbar>{renderDefaultToolbar((slots) => transformToolbar({ ...slots, Download }))}</Toolbar>
  );

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar,
  });

  const { renderDefaultToolbar } = defaultLayoutPluginInstance.toolbarPluginInstance;

  const jumpToPagePluginInstance = jumpToPagePlugin();
  const { jumpToPage } = jumpToPagePluginInstance;
  useEffect(() => {
    if (isShowing) {
      jumpToPage(realCurrentPage);
    }
  }, [isShowing, jumpToPage, realCurrentPage]);

  if (!isValid) {
    return <>{renderError({ name: 'MissingPDFException' })}</>;
  }

  if (isPDFFormat(fileType)) {
    // Check if URL is valid before rendering
    if (!file_url || file_url.trim() === '') {
      return <>{renderError({ name: 'MissingPDFException' })}</>;
    }

    return (
      <Box h='100%'>
        <Box h='100%'>
          <Viewer
            fileUrl={file_url}
            plugins={[defaultLayoutPluginInstance, jumpToPagePluginInstance, getFilePluginInstance]}
            initialPage={realCurrentPage}
            renderLoader={renderLoader}
            renderError={renderError}
          />
        </Box>
      </Box>
    );
  }

  if (isSeperatedValueFormat(fileType)) {
    return (
      <Box overflowY='scroll' w='100%' h='100%' fontSize={14}>
        <Table size={'xl'} variant='simple'>
          <Tbody>
            {csvContent?.map((row, rowIndex) => (
              <Tr key={rowIndex}>
                {row?.map((cell, cellIndex) => (
                  <Td key={cellIndex} p={1} border='border_table_grid'>
                    {cell}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  }

  if (isMarkdownFormat(fileType) || isTxtFormat(fileType)) {
    return (
      <Box
        className='markdown'
        overflowY='scroll'
        w='100%'
        h='100%'
        px={6}
        pb={4}
        color='black'
        bg='white'
        border='1px'
      >
        <Markdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
          {markdownContent}
        </Markdown>
      </Box>
    );
  }

  if (isOfficeSupportedType(fileType)) {
    return (
      <iframe
        src={getOfficeLivePreviewURL(file_url)}
        width='100%'
        height='100%'
        title='PDF Viewer'
      />
    );
  }

  return (
    <>
      {renderError(
        { name: 'ExtensionNotSupported' },
        { url: file_url, name: file_name, type: fileType }
      )}
    </>
  );
};

const FilePreview = ({ onClose, data, isValid }: FilePreviewProps) => {
  const currentPageRef = useRef(1);

  const [tabIndex, setTabIndex] = useState(0);
  const [csvContent, setCsvContent] = useState<string[][]>([]);
  const [markdownContent, setMarkdownContent] = useState('');

  const fileType = data?.fileType?.toLowerCase() as DOCUMENT_TYPE;

  const handleTabsChange = (index: number) => {
    setTabIndex(index);
  };

  const onClickSupportContent = (pageNumber: string) => {
    if (data?.fileType?.toLowerCase() === DOCUMENT_TYPE.PDF) {
      currentPageRef.current = parseInt(pageNumber);
    }
    setTabIndex(1);
  };

  useEffect(() => {
    currentPageRef.current = 1;
    if (data?.fileType === DOCUMENT_TYPE.CSV) {
      Papa.parse<string[]>(data?.file_url, {
        download: true,
        skipEmptyLines: true,
        complete: function (results) {
          setCsvContent(results.data);
        },
      });
    }
    if (isMarkdownFormat(fileType) || isTxtFormat(fileType)) {
      request({
        url: data?.file_url,
        method: HTTPMethod.GET,
        transformRequest: [
          (data, headers) => {
            delete headers['Authorization'];
            return data;
          },
        ],
      })
        .then((data) => setMarkdownContent(data as string))
        .catch((e) => {
          console.log(e);
        });
    }
  }, [data]);

  return (
    <>
      <Box
        pos='relative'
        w='100%'
        h='100%'
        ms='16px'
        bg='bg_main_container'
        borderRadius='20px'
        shadow='shadow_box'
      >
        <Tooltip label='Close preview'>
          <Box
            pos='absolute'
            zIndex={999}
            top='2'
            right='5'
            _hover={{ opacity: '0.8' }}
            cursor='pointer'
          >
            <CloseButton onClick={onClose} size='md' />
          </Box>
        </Tooltip>

        <Box h='100%' ms='10px'>
          <Tabs h='100%' index={tabIndex} onChange={handleTabsChange} variant='unstyled'>
            <TabList>
              <Tab _focus={{ boxShadow: 'none' }} _selected={{ fontWeight: 600 }}>
                {t('chat.supportingContent')}
              </Tab>
              <Tab _focus={{ boxShadow: 'none' }} _selected={{ fontWeight: 600 }}>
                {t('chat.citation')}
              </Tab>
            </TabList>

            <TabIndicator h='2px' mt='-1.5px' bg='blue.500' borderRadius='1px' />
            <TabPanels overflow='auto' h='94%'>
              <TabPanel>
                <Stack>
                  {data?.supporting_contents?.map((content) => (
                    <Box
                      key={content.text + content.page}
                      p='15px'
                      bg='bg_box'
                      borderRadius='10px'
                      shadow='shadow_box'
                      _hover={{
                        transform: 'scale(1.03)',
                        cursor: 'pointer',
                      }}
                      onClick={() => onClickSupportContent(content?.page)}
                    >
                      <Box mb='10px' fontSize='16px' fontWeight='600'>
                        {data?.file_name}
                      </Box>
                      <Box fontSize='14px'>
                        {t('common.page')}: {content?.page}
                      </Box>
                      <Box w='100%' fontSize='14px'>
                        {content?.text}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </TabPanel>
              <TabPanel h='100%'>
                <FilePreviewRender
                  data={data}
                  isValid={isValid}
                  csvContent={csvContent}
                  markdownContent={markdownContent}
                  currentPage={currentPageRef.current}
                  isShowing={tabIndex === 1}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>
    </>
  );
};

export default FilePreview;
